import { Button, message } from 'antd'
import Picker, { EmojiClickData, EmojiStyle, SkinTonePickerLocation } from "emoji-picker-react";
import { KeyboardEvent, useEffect, useRef, useState } from 'react'
import { Mention, MentionsInput } from 'react-mentions'
import { insertTextarea } from '../../../../../shared/utils/functions/insertTextIntoTextarea';
import './mentionsInput.scss';
import { useAddNewCommentMutation, useEditCommentMutation } from '../../../slice/candiddateApiSlice';
import { useParams } from 'react-router-dom';
import { errorMessage } from '../../../../../shared/utils/constantData';
import LazyLoader from '../../../../../shared/components/LazyLoader/load';
import ProfileImage from '../../../../../shared/components/NavBar/profileImage';
import { IMentionMember } from '../../../../../shared/utils/interfaces/AuthInterface';
import { useDispatch, useSelector } from 'react-redux';
import { selectInputCommentValue, selectIsEditingComment, selectMentions, selectSelectedCommentId, setInputCommentValue, setIsEditingComment, setSelectedCommentId } from '../../../slice/candidateSlice';
import { logEvent } from 'firebase/analytics';
import { analytics } from '../../../../../shared/utils/analytics';
import { getImageUrl } from '../../../../../shared/utils/functions/images';
import postCommentIcon from "../../../../../shared/assets/images/postComment.svg"

export default function AddComment({ users, handleCancelEdit }: { users: IMentionMember[], handleCancelEdit: () => void }) {
    const dispatch = useDispatch();
    const isEditing = useSelector(selectIsEditingComment);
    const selectedCommentId = useSelector(selectSelectedCommentId);
    const [mentions, setMentions] = useState<IMentionMember[]>([]);
    const [showPicker, setShowPicker] = useState(false);
    const mentionsInputRef = useRef<HTMLTextAreaElement>(null);
    const addCommentRef = useRef<HTMLDivElement>(null);
    const [pickerWidth, setPickerWidth] = useState(350);
    const { applicationId } = useParams();
    const [addComment, { data, isLoading, isSuccess, isError }] = useAddNewCommentMutation();
    const [editComment, { data: editData, isLoading: isEditLoading, isSuccess: isEditSuccess, isError: isEditError }] = useEditCommentMutation();
    const mentionables = useSelector(selectMentions);
    const value = useSelector(selectInputCommentValue);

    useEffect(() => {
        return () => {
            dispatch(setInputCommentValue(''));
            dispatch(setIsEditingComment(false));
        }
    }, [dispatch])

    // Keep the emoji picker no wider than the comment box so it never overflows its container
    useEffect(() => {
        const container = addCommentRef.current;
        if (!container) return;

        const updatePickerWidth = () => {
            const containerWidth = container.offsetWidth;
            setPickerWidth(Math.max(280, Math.min(350, containerWidth - 24)));
        };

        updatePickerWidth();
        const resizeObserver = new ResizeObserver(updatePickerWidth);
        resizeObserver.observe(container);
        return () => resizeObserver.disconnect();
    }, [])

    useEffect(() => {
        if (isSuccess) {
            dispatch(setInputCommentValue(''));
            if (mentionsInputRef?.current) mentionsInputRef?.current?.focus();
        }
        else if (isError) message.error(errorMessage, 5);
    }, [isSuccess, isError, data, dispatch])

    useEffect(() => {
        if (isEditSuccess) {
            dispatch(setInputCommentValue(''));
            dispatch(setIsEditingComment(false));
            dispatch(setSelectedCommentId(null));
            if (mentionsInputRef?.current) mentionsInputRef?.current?.focus();
        }
        else if (isEditError) message.error(errorMessage, 5);
    }, [isEditSuccess, isEditError, editData, dispatch])

    // Function for maintaining correct mentions markup upon inserting @ sign or emoji
    const handleMentionsOnInsert = (textareaValue: string) => {
        const maintainedValue = textareaValue
            .split(' ')
            .map(word => {
                // If word starts with @ and it exists on users array, add brackets and id so react-mentions can handle
                // mentions correctly upon mention or emoji insertion
                const userExists = users.filter(({ display }) => display === word.slice(1))[0];
                return word.startsWith('@') && userExists ?
                    `@[${userExists?.display}](${userExists?.id})`
                    :
                    word
            })
            .join(' ');
        dispatch(setInputCommentValue(maintainedValue));
    }

    const handleChange = (_: any, newValue: string) => {
        dispatch(setInputCommentValue(newValue));
        handleDeletedMentions(newValue);
    };

    const handleDeletedMentions = (newValue: string) => {
        const regex = /@\[([^\]]+)\]\(\d+\)/g;
        const newMentions: IMentionMember[] = [];

        let match;
        while ((match = regex.exec(newValue)) !== null) {
            const display = match[1];
            const existingMention = mentions.find(mention => mention.display === display);
            if (existingMention) {
                newMentions.push(existingMention);
            }
        }

        setMentions(newMentions);
    };

    const handleUserSelect = (id: string | number, display: string) => {
        const mentionObject = mentionables.find(mention => mention.id === id)
        const newMentions = [...mentions, {
            id,
            display,
            email: mentionObject ? mentionObject?.email : ''
        }];
        setMentions(newMentions);
    };
    const onEmojiClick = (emojiObject: EmojiClickData) => {
        if (emojiObject.emoji) {
            const newTextareaValue = insertTextarea(mentionsInputRef.current, emojiObject.emoji);
            handleMentionsOnInsert(newTextareaValue);
        }
        setShowPicker(false);
    };

    const handleMentionSomeone = () => {
        const textarea = mentionsInputRef.current;
        if (!textarea) return;

        const position = textarea.selectionStart;
        const prevChar = textarea.value.charAt(position - 1);

        if (prevChar !== "@") {
            const mentionChar = prevChar === ' ' ? '@' : ' @';
            const newTextareaValue = insertTextarea(textarea, mentionChar);
            handleMentionsOnInsert(newTextareaValue);
        } else {
            textarea.setSelectionRange(position, position);
            textarea.focus();
        }
    }

    const handleApply = async () => {
        const commentBody = {
            applicationId: Number(applicationId),
            text: value,
            mentionedUserIds: mentions?.map(({ id }) => id)
        }
        isEditing ? await editComment({
            id: selectedCommentId,
            comment: commentBody?.text,
            mentionedUserIds: commentBody?.mentionedUserIds
        }) : await addComment(commentBody);
        logEvent(analytics, `${isEditing ? 'Edit' : 'Add new'} comment`)
    };

    const handleEnterPress = (event: KeyboardEvent) => {
        if (event.key === 'Enter' && event.shiftKey === false && value?.trim()) {
            event.preventDefault();
            handleApply();
        }
    }

    return (
        <>
            <div ref={addCommentRef} className={`relative flex items-center flex-grow-0 ${isEditing ? 'flex-wrap' : 'flex-nowrap'}`}>
                <div className='relative flex items-center flex-grow'>
                    <MentionsInput
                        value={value}
                        disabled={isLoading}
                        onChange={handleChange}
                        forceSuggestionsAboveCursor
                        className='mentions-input'
                        placeholder='Add comment..'
                        inputRef={mentionsInputRef}
                        onKeyDown={handleEnterPress}
                    >
                        <Mention
                            className='relative z-10 text-indigo-700'
                            trigger="@"
                            data={users}
                            displayTransform={(_, display) => `@${display}`}
                            markup="@[__display__](__id__)"
                            onAdd={(id, display) => handleUserSelect(id, display)}
                            appendSpaceOnAdd
                            renderSuggestion={({ name, email }: any, _, __, index, focused) => {
                                return <li key={index} className={`p-2 hover:bg-indigo-50 ${focused ? 'bg-indigo-50' : ''}`}>
                                    <div className='flex items-center flex-nowrap'>
                                        <ProfileImage name={name ? name : email} className='flex-shrink-0 me-3.5' />
                                        <div className='min-w-0 flex-1'>
                                            <p className='text-sm font-semibold leading-5 text-gray-600 truncate'>{name ? name : email}</p>
                                            <p className='text-xs font-normal leading-5 text-gray-400 truncate'>{email}</p>
                                        </div>
                                    </div>
                                </li>
                            }}
                        />
                    </MentionsInput>
                    <div className="absolute flex gap-1.5 right-3 min-w-max cursor-pointer">
                        <img alt='Mention someone'
                            src={getImageUrl('mentionIcon')}
                            onClick={handleMentionSomeone}
                        />
                        <img alt='Emojis'
                            src={getImageUrl('emojiIcon')}
                            onClick={() => setShowPicker(!showPicker)}
                        />
                    </div>
                    <LazyLoader className={`${isLoading ? 'opacity-1' : 'opacity-0'} min-h-[0.25rem] absolute top-0 z-20 !rounded-bl-none !rounded-br-none`} />
                </div>
                <div className={`${isEditing ? 'md:w-fit md:inline w-full flex flex-row-reverse mt-3 md:mt-0' : 'inline'}`}>

                    <Button disabled={isLoading || value?.length === 0 || isEditLoading} className='btn-link' onClick={handleApply}>
                        <span className={`${isEditing ? '' : '!hidden'} md:!inline`}>{isEditing ? 'Update' : 'Post'}</span>
                        {isEditing ? <></>
                            : <img
                                className={`!inline md:!hidden ms-1 ${isLoading || value?.length === 0 ? 'grayscale opacity-30' : ''}`}
                                src={postCommentIcon}
                                alt="post"
                            />}
                    </Button>
                    {isEditing && <Button className={`btn-link ${isEditLoading ? 'grayscale opacity-30' : ''}`} disabled={isEditLoading} onClick={handleCancelEdit}>
                        <span className="text-gray-600 ">Cancel</span>
                    </Button>}
                </div>
                <Picker
                    className='!absolute bottom-[3.75rem] right-3 z-20 max-w-full'
                    width={pickerWidth}
                    onEmojiClick={onEmojiClick}
                    emojiStyle={EmojiStyle.NATIVE}
                    lazyLoadEmojis
                    skinTonePickerLocation={SkinTonePickerLocation.PREVIEW}
                    open={showPicker}
                />
            </div>
        </>
    )
}
